import { useQuery } from '@tanstack/react-query';
import axiosClient from '../api/axiosClient';

interface StoryblokModeResponse {
  storyblokOnly: boolean;
  flags: Record<string, unknown>;
  env: string;
}

/**
 * Single source of truth for "is this tenant in Storyblok-only mode?".
 *
 * Delegates all logic to GET /api/tenant/:code/storyblok-mode (server-side).
 * The server mirrors the rule from mobile/benefits-mobile-app/src/hooks/useStoryblokGate.ts:
 * the tenant attribute `showStoryBlokOnly`, in every environment (CPT-1415 —
 * the Mixpanel flag `enable_storyblok_content` no longer decides anything).
 *
 * Returns:
 *   storyblokOnly — true = Storyblok only; false = legacy CMS
 *   ready         — false while the query is still in flight
 *
 * Fail-open: on query error, storyblokOnly=false so we never block the UI.
 */
export function useStoryblokMode(tenantCode: string): { storyblokOnly: boolean; ready: boolean } {
  const { data, isSuccess, isError } = useQuery<StoryblokModeResponse>({
    queryKey: ['storyblok-mode', tenantCode],
    queryFn: () =>
      axiosClient.get<StoryblokModeResponse>(`/api/tenant/${tenantCode}/storyblok-mode`).then((r) => r.data),
    enabled: !!tenantCode,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const ready = isSuccess || isError;
  const storyblokOnly = data?.storyblokOnly ?? false;

  return { storyblokOnly, ready };
}
